import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { startGoogleSignIn } from "../lib/googleAuth";
import { COUNTRIES } from "../lib/countries";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Smartphone, Gamepad2, Eye, EyeOff } from "lucide-react";

/**
 * V2 AuthGate — mobile-first dark login/register surface that lives at /v2.
 * Replaces the V1 redirect to /login so the Play Store TWA can launch
 * directly at start_url=/v2 without bouncing the user to a desktop-styled
 * login page.
 *
 * Modes:
 *   - "signin"  : username + password OR Google
 *   - "signup"  : full register form (mobile-first compressed) OR Google
 *
 * On success, the parent <V2Gate> (in App.js) re-renders and lets the user
 * through to <MobileShell />.
 */
export default function AuthGate() {
  const { t } = useT();
  const [mode, setMode] = useState("signin");

  // Persist intent so the Google OAuth callback can bounce us back to /v2
  // instead of /dashboard. Cleared by GoogleAuthCallback after redirect.
  useEffect(() => {
    try { localStorage.setItem("eplay:postLoginRedirect", "/v2"); } catch { /* noop */ }
  }, []);

  return (
    <div
      data-testid="v2-auth-gate"
      className="fixed inset-0 z-40 bg-[#070710] text-white overflow-y-auto font-sans"
    >
      <div className="relative mx-auto w-full max-w-[480px] min-h-full flex flex-col px-5 pt-8 pb-10 bg-[#070710] border-x border-[#1F1F2A]">
        {/* Brand */}
        <div className="flex items-center gap-2.5 mb-8">
          <img
            src="/logo-256.png"
            alt="ePlay"
            data-testid="v2-authgate-logo"
            className="w-10 h-10 rounded-xl object-contain shadow-lg shadow-[#7C3AED]/40 ring-1 ring-white/10"
          />
          <div className="leading-tight">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#A5B4FC]">ePlay</div>
            <div className="text-base font-bold text-white">Master League</div>
          </div>
        </div>

        {/* Hero */}
        <div className="mb-6">
          <div className="text-[11px] font-medium uppercase tracking-[0.25em] text-[#A5B4FC]">
            {mode === "signin" ? t("v2.auth.kicker.signin") : t("v2.auth.kicker.signup")}
          </div>
          <h1 className="text-3xl font-bold text-white mt-1">
            {mode === "signin" ? t("v2.auth.title.signin") : t("v2.auth.title.signup")}
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1.5">
            {mode === "signin" ? t("v2.auth.sub.signin") : t("v2.auth.sub.signup")}
          </p>
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 p-1 bg-[#0F0F18] rounded-2xl border border-[#1F1F2A] mb-6">
          <ModeTab active={mode === "signin"} onClick={() => setMode("signin")} testId="v2-auth-tab-signin">
            {t("v2.auth.tab.signin")}
          </ModeTab>
          <ModeTab active={mode === "signup"} onClick={() => setMode("signup")} testId="v2-auth-tab-signup">
            {t("v2.auth.tab.signup")}
          </ModeTab>
        </div>

        {/* Form */}
        {mode === "signin" ? <SignInForm /> : <SignUpForm onSwitch={() => setMode("signin")} />}

        {/* Divider */}
        <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] font-mono text-[#52525B]">
          <span className="flex-1 h-px bg-[#1F1F2A]" />
          {t("v2.auth.or")}
          <span className="flex-1 h-px bg-[#1F1F2A]" />
        </div>

        {/* Google */}
        <button
          type="button"
          onClick={startGoogleSignIn}
          data-testid="v2-auth-google"
          className="w-full inline-flex items-center justify-center gap-3 rounded-2xl border border-[#27272F] bg-[#0F0F18] hover:bg-[#15151F] text-white font-medium text-sm py-3.5 transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {t("v2.auth.googleCta")}
        </button>

        <div className="mt-auto pt-8 text-center">
          <p className="text-[11px] text-[#52525B] font-mono uppercase tracking-[0.2em]">
            {t("v2.auth.footer")}
          </p>
        </div>
      </div>
    </div>
  );
}

function ModeTab({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
        active
          ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white shadow-lg shadow-[#7C3AED]/30"
          : "text-[#71717A] hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function SignInForm() {
  const { login } = useAuth();
  const { t } = useT();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(username.trim(), password);
      toast.success(t("login.toast"));
      // Parent <V2Gate> will re-render with the authenticated MobileShell.
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="v2-auth-signin-form">
      <V2Field label={t("login.username")}>
        <input
          data-testid="v2-auth-signin-username"
          autoComplete="username"
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full bg-transparent text-white text-sm placeholder-[#52525B] focus:outline-none"
          placeholder={t("v2.auth.placeholder.username")}
        />
      </V2Field>
      <V2Field label={t("login.password")} trailing={
        <button type="button" onClick={() => setShowPw((s) => !s)} className="text-[#71717A] hover:text-white" tabIndex={-1}>
          {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      }>
        <input
          data-testid="v2-auth-signin-password"
          type={showPw ? "text" : "password"}
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-transparent text-white text-sm placeholder-[#52525B] focus:outline-none"
          placeholder="••••••••"
        />
      </V2Field>
      {err && (
        <div className="text-xs text-[#F87171] font-mono" data-testid="v2-auth-signin-error">// {err}</div>
      )}
      <button
        type="submit"
        disabled={busy}
        data-testid="v2-auth-signin-submit"
        className="w-full rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white font-bold text-sm py-3.5 shadow-lg shadow-[#7C3AED]/30 hover:opacity-95 disabled:opacity-50 transition-opacity"
      >
        {busy ? t("login.submitting") : t("login.submit")}
      </button>
    </form>
  );
}

const PLATFORMS = {
  mobile: { Icon: Smartphone, accent: "#7C3AED" },
  console: { Icon: Gamepad2, accent: "#10B981" },
};

function SignUpForm({ onSwitch }) {
  const { register } = useAuth();
  const { t } = useT();
  const [platform, setPlatform] = useState("mobile");
  const [username, setUsername] = useState("");
  const [gameId, setGameId] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [nationality, setNationality] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register(username.trim(), gameId.trim(), whatsapp.trim(), password, platform);
      // Best-effort: nationality is optional
      if (nationality) {
        try { await api.patch("/users/me/profile", { nationality }); } catch { /* noop */ }
      }
      toast.success(t("reg.toast"));
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="v2-auth-signup-form">
      {/* Platform picker */}
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(PLATFORMS).map(([key, p]) => {
          const active = platform === key;
          const Icon = p.Icon;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setPlatform(key)}
              data-testid={`v2-auth-signup-platform-${key}`}
              className={`rounded-2xl border py-3 flex flex-col items-center justify-center gap-1 transition-all ${
                active
                  ? "border-[#7C3AED] bg-[#7C3AED]/15 text-white"
                  : "border-[#1F1F2A] bg-[#0F0F18] text-[#71717A] hover:text-white"
              }`}
              style={active ? { boxShadow: `0 0 0 1px ${p.accent}66` } : undefined}
            >
              <Icon className="w-4 h-4" />
              <span className="text-[11px] font-bold uppercase tracking-wider">{t(`v2.auth.platform.${key}`)}</span>
            </button>
          );
        })}
      </div>

      <V2Field label={t("login.username")}>
        <input
          data-testid="v2-auth-signup-username"
          required minLength={2} maxLength={24}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full bg-transparent text-white text-sm placeholder-[#52525B] focus:outline-none"
          placeholder={t("v2.auth.placeholder.username")}
        />
      </V2Field>

      <V2Field label={t("v2.auth.gameid")}>
        <input
          data-testid="v2-auth-signup-gameid"
          required
          value={gameId}
          onChange={(e) => setGameId(e.target.value)}
          className="w-full bg-transparent text-white text-sm placeholder-[#52525B] focus:outline-none"
          placeholder={t("v2.auth.placeholder.gameid")}
        />
      </V2Field>

      <V2Field label={t("v2.auth.whatsapp")}>
        <input
          data-testid="v2-auth-signup-whatsapp"
          required
          inputMode="tel"
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          className="w-full bg-transparent text-white text-sm placeholder-[#52525B] focus:outline-none"
          placeholder={t("v2.auth.placeholder.whatsapp")}
        />
      </V2Field>

      <V2Field label={t("login.password")} trailing={
        <button type="button" onClick={() => setShowPw((s) => !s)} className="text-[#71717A] hover:text-white" tabIndex={-1}>
          {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      }>
        <input
          data-testid="v2-auth-signup-password"
          type={showPw ? "text" : "password"}
          required minLength={6}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-transparent text-white text-sm placeholder-[#52525B] focus:outline-none"
          placeholder="••••••••"
        />
      </V2Field>

      <V2Field label={t("v2.auth.nationality")}>
        <select
          data-testid="v2-auth-signup-nationality"
          value={nationality}
          onChange={(e) => setNationality(e.target.value)}
          className="w-full bg-transparent text-white text-sm focus:outline-none"
        >
          <option value="" className="bg-[#0F0F18]">{t("v2.auth.nationality.placeholder")}</option>
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code} className="bg-[#0F0F18]">{c.name}</option>
          ))}
        </select>
      </V2Field>

      {err && (
        <div className="text-xs text-[#F87171] font-mono" data-testid="v2-auth-signup-error">// {err}</div>
      )}

      <button
        type="submit"
        disabled={busy}
        data-testid="v2-auth-signup-submit"
        className="w-full rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white font-bold text-sm py-3.5 shadow-lg shadow-[#7C3AED]/30 hover:opacity-95 disabled:opacity-50 transition-opacity"
      >
        {busy ? t("v2.auth.creating") : t("v2.auth.create")}
      </button>

      <button
        type="button"
        onClick={onSwitch}
        className="w-full text-center text-xs text-[#A5B4FC] hover:text-white transition-colors py-1"
      >
        {t("v2.auth.haveAccount")}
      </button>
    </form>
  );
}

function V2Field({ label, children, trailing }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717A] mb-1.5">{label}</div>
      <div className="flex items-center gap-2 rounded-2xl bg-[#0F0F18] border border-[#1F1F2A] focus-within:border-[#7C3AED] px-4 py-3 transition-colors">
        <div className="flex-1 min-w-0">{children}</div>
        {trailing}
      </div>
    </label>
  );
}
